import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Alert,
  } from "@mui/material";
  import { useEffect, useState } from "react";
  import { useAddUnitTypeMutation, useUpdateUnitTypeMutation } from "../api/unitTypesApi";
  import { UnitType } from "../types/unitType";
  
  interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    unitType?: UnitType | null;
  }
  
  const AddEditUnitType: React.FC<Props> = ({ open, setOpen, unitType }) => {
    const [formData, setFormData] = useState({
      name: "",
    });
  
    const [formErrors, setFormErrors] = useState({
      name: "",
    });
  
    const [addUnitType, { isLoading: isAdding }] = useAddUnitTypeMutation();
    const [updateUnitType, { isLoading: isUpdating }] = useUpdateUnitTypeMutation();
    const [submissionError, setSubmissionError] = useState<string | null>(null);
  
    useEffect(() => {
      if (unitType) {
        setFormData({
          name: unitType.name || "",
        });
      } else {
        setFormData({ name: "" });
      }
      setFormErrors({ name: "" });
    }, [unitType]);
  
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: "" })); // Clear error on change
    };
  
    const validateForm = () => {
      let valid = true;
      const errors = { name: "" };
  
      if (!formData.name.trim()) {
        errors.name = "Name is required";
        valid = false;
      }
  
      setFormErrors(errors);
      return valid;
    };
  
    const handleSubmit = async () => {
      if (!validateForm()) return;
  
      try {
        if (unitType) {
          await updateUnitType({ id: unitType.id, body: formData }).unwrap();
        } else {
          await addUnitType({ body: formData }).unwrap();
        }
  
        setFormData({ name: "" });
        setFormErrors({ name: "" });
        setSubmissionError(null);
        setOpen(false);
      } catch (error: any) {
        setSubmissionError("Failed to save Unit Type. Please try again.");
      }
    };
  
    const handleCloseError = () => {
      setSubmissionError(null);
    };
  
    return (
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{unitType ? "Edit Unit Type" : "Add New Unit Type"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
  
            {submissionError && (
              <Alert severity="error" onClose={handleCloseError}>
                {submissionError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setFormData({ name: "" });
              setFormErrors({ name: "" });
            }}
            disabled={isAdding || isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isAdding || isUpdating}
          >
            {unitType ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  export default AddEditUnitType;
  